// ReportContext.tsx
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Face, FacialData } from './PersonalReport/Interface/FaceInterface';
import { SkinData } from './PersonalReport/Interface/SkinInterface';
import config from "../config";
import { logger } from '../utils/logger';

const MAXIMUM_REPORTS_PER_CUSTOMER = 15;

export interface Report {
  reportId: string;        // Unique identifier for the report
  image?: File;             // The image file associated with the report
  reportDetail: string;    // A JSON string containing the detailed report
  createdAt: string;       // The creation time of the report, formatted as a string
  isDefault: boolean;        // The boolean flag whether it is a default profile to be used in ai recommendation
  imageKey?: string;   // the imagekey mapping to the image file saved in S3 bucket
  imagePresignedUrl?: string;  // s3 presignedurl to display image directly
}

interface DraftReport {
  report_id: string;
  report_detail: string;
  image_key: string;
  created_at: string;
  is_default: boolean;
}

interface ReportContextType {
  defaultReport: Report | null;
  sortedReports: Array<Report>;
  createReport: (report: Report) => void;  // create new report
  changeDefaultReport: (report: Report) => void;
  deleteReport: (report: Report) => void;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return context;
};

export const generateReportJson = (face: Face, facialData: FacialData, skinData: SkinData) => {
   // Generate a unique reportId using UUID
  const report = {
    face,
    facialData,
    skinData
  };
    // Convert the report object to JSON
  const reportJson = JSON.stringify(report);
  return reportJson;
}

// Function to parse a report from JSON string
export const parseReportJson = (reportJson: string): { face: Face, facialData: FacialData, skinData: SkinData} => {
  try {
    const report = JSON.parse(reportJson);
    // Return the parsed report
    return report;
  } catch (error) {
    throw new Error('Failed to parse report JSON');
  }
};

export const ReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cachedReports, setCachedReports] = useState<Array<Report>>([]);
  const [defaultReport, setDefaultReport] = useState<Report|null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>(() => localStorage.getItem('email') || '');

  // 监听localStorage变化，刷新email
  useEffect(() => {
    const syncEmail = () => {
      const storedEmail = localStorage.getItem('email') || '';
      setEmail(storedEmail);
    };
    window.addEventListener('storage', syncEmail);
    // 可选：定时器兜底
    const interval = setInterval(syncEmail, 2000);
    return () => {
      window.removeEventListener('storage', syncEmail);
      clearInterval(interval);
    };
  }, []);

  const shadowLoadReportLists = async (email: string): Promise<Report[]> => {
    try {
       const response = await fetch(`${config.REPORT_API_BASE_URL}/users/${encodeURIComponent(email)}/reportIds`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch report lists: ${response.status} - ${response.statusText}`);
      }
      const data = await response.json();
      // shadow load report to get created_at, report_id, is_default info only
      const reports: Report[] = data.map((item: any) => ({
        reportId: item.report_id,
        image: item.image_key,
        reportDetail: item.report,
        createdAt: item.created_at,
        isDefault: item.hasOwnProperty('is_default') ? item.is_default : false
      }));
      return reports;
    } catch (error) {
      throw new Error(`Error loading report lists: ${error}`);
      return [];
    }
  };

  //   const getLatestFacialData = () => {
//     if (savedReports && savedReports.length > 0) {
//       try {
//         const parsedReport = parseReportJson(savedReports[0].reportDetail);
//         return parsedReport.facialData;
//       } catch (error) {
//         console.error('Error parsing the latest report detail:', error);
//         return null;
//       }
//     }
//     return null;
//   };

  const createReport = async (report: Report) => {
    if (cachedReports.length >= MAXIMUM_REPORTS_PER_CUSTOMER) {
      alert(`Fail to create new report as total number of reports exceed ${MAXIMUM_REPORTS_PER_CUSTOMER}`);
      return;
    }
    saveReport(report, true);
    if (!defaultReport) {
      setDefaultReport(report);
    }
  }

  const changeDefaultReport = async (report: Report) => {
    if (!defaultReport) {
        console.info("No existing default report found.");
        setDefaultReport(report);
        saveReport(report, false);
        return;
    }
    if (defaultReport.reportId === report.reportId) return;
    const prevReport = { ...defaultReport, isDefault: false };
    try {
        const prevReportSaved = await saveReport(prevReport, false);
        const newReportSaved = await saveReport(report, false);
        setDefaultReport(report);
    } catch (error) {
        logger.error(`Error while updating default report: ${error}`);
        throw error;
    }
  };

  const deleteReport = async (report: Report) => {
      const payload = {
            email,
            report_id: report.reportId
        };
      // Prepare the payload for the Lambda endpoint
      const response = await fetch(`${config.REPORT_API_BASE_URL}/users/${encodeURIComponent(email)}/delete-report`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',  // Send JSON data
          },
          body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete report: ${response.statusText}`);
      }

      // Remove the report from cached reports and handle default report case
      setCachedReports(prevReports => {
        const updatedReports = prevReports.filter(r => r.reportId !== report.reportId);
        
        // Handle default report case
        if (report.isDefault && updatedReports.length > 0) {
          // If we're deleting the default report, set the first remaining report as default
          const newDefaultReport = { ...updatedReports[0], isDefault: true };
          setDefaultReport(newDefaultReport);
          // Update the first report to be default in the backend (async)
          saveReport(newDefaultReport, false).catch(error => {
            logger.error('Failed to update new default report:', error);
          });
          return updatedReports.map((r, index) => 
            index === 0 ? newDefaultReport : r
          );
        } else if (report.isDefault && updatedReports.length === 0) {
          // No reports left
          setDefaultReport(null);
        }
        
        return updatedReports;
      });
  };

  // 将新报告追加到列表中
  const saveReport = async (report: Report, shouldSaveImage: boolean = true) => {
      try {
           let payload = {
                email,
                report_id: report.reportId,
                report_detail: report.reportDetail,
                created_at: report.createdAt,
                is_default: report.isDefault,
                image_key: report.imageKey,
            };

           if (shouldSaveImage && report.image) {
               logger.log("saving image to s3")
               const toBase64 = (file: File): Promise<string> =>
                    new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.readAsDataURL(file);
                      reader.onload = () => resolve((reader.result as string).split(',')[1]); // Remove "data:image/*;base64,"
                      reader.onerror = (error) => reject(error);
                });

              const base64Image = await toBase64(report.image);

              //Upload image to s3 and get the related image key
              const imageResponse = await fetch(`${config.S3_IMAGE_API_BASE_URL}/users/${encodeURIComponent(email)}/report/images`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',  // Send JSON data
                  },
                  body: JSON.stringify({email, report_id:report.reportId, image_base64: base64Image}),
              });
              const data = await imageResponse.json();
              payload.image_key = data.image_key;
              report.imageKey = data.image_key;
              report.imagePresignedUrl = data.image_presigned_url;
              logger.log(`saving image to s3 successfully with image_key ${data.image_key}`)
           }

           logger.log(`checking image_key ${payload.image_key} before saving into db`)

          // Prepare the payload for the Lambda endpoint
          const response = await fetch(`${config.REPORT_API_BASE_URL}/users/${encodeURIComponent(email)}/reports`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',  // Send JSON data
              },
              body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`Failed to save report: ${response.statusText}`);
          }

          setCachedReports(prevReports => {
            const existingReportIndex = prevReports.findIndex(r => r.reportId === report.reportId);
            if (existingReportIndex !== -1) {
              // Replace the existing report
              const updatedReports = [...prevReports];
              updatedReports[existingReportIndex] = report;
              return updatedReports;
            }
            logger.log("adding new report into cachedreports", report)
            return [...prevReports, report];
          });
      } catch (error) {
         throw new Error(`Failed to save report: ${error}`); // 抛出错误
      }
    };

    const fetchReports = async (email:string, reportIds:Array<string>): Promise<Report[]> => {
      try {
        const response = await fetch(`${config.REPORT_API_BASE_URL}/users/${encodeURIComponent(email)}/reports`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json'},
          body: JSON.stringify({
            reportIds,
            email
          }),
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch report with ID ${reportIds}: ${response.statusText}`);
        }

        const reports: DraftReport[] = await response.json();
        if (reports.length === 0) {
          logger.warn("No reports found.");
          return [];
        }

        // Extract image_keys from reports
        const imageKeys: string[] = reports.map((report) => report.image_key);
        // Download images from s3 based on the image_key list from response
        const s3ImageMap: Map<string, string> = await downloadS3Images(imageKeys);

         // Process each report's image to decode base64 and create a File object
        const processedReports: Report[] = reports.map((report) => {
          const { report_id, image_key, report_detail, created_at, is_default} = report;
          const image_presigned_url= s3ImageMap.get(image_key) ?? undefined;
          logger.log("report_id and is_default and image_key and image_presigned_url are: ", report_id, is_default, image_key, image_presigned_url)
          if (!image_presigned_url) {
            logger.log("S3 Image Map as Array:", Array.from(s3ImageMap.entries()));
            logger.error(`Image key '${image_key}' not found in S3 image map.`);
          }
          return {
            reportId: report_id,
            imageKey: image_key,
            imagePresignedUrl: image_presigned_url,
            reportDetail: report_detail,
            createdAt: created_at,
            isDefault: is_default,
          } as Report;
        });

        return processedReports;  // Return the array of processed reports
      } catch (error) {
        logger.error(`Error fetching report ${reportIds}:`, error);
        return [];
      }
    };

    const downloadS3Images = async (imageKeys: string[]): Promise<Map<string, string>> => {
      try {
        const response = await fetch(
          `${config.S3_IMAGE_API_BASE_URL}/users/${encodeURIComponent(email)}/report/images`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageKeys }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to download S3 images: ${response.status} - ${response.statusText}`);
        }

        const data:Array<{ image_key: string; image_presigned_url: string }>= await response.json(); // Expected response format: { "imageKey1": "base64Data1", "imageKey2": "base64Data2" }
        // Convert response to a Map
        const imageMap = new Map<string, string>();
        data.forEach(item => {
          imageMap.set(item.image_key, item.image_presigned_url);
        });
        return imageMap;
      } catch (error) {
        logger.error("Error downloading S3 images:", error);
        return new Map(); // Return an empty map on failure
      }
    };

    // Function to load reports initially
  useEffect(() => {
    const loadInitialReports = async () => {
      try {
        const shadowLoadReports = await shadowLoadReportLists(email);
        const reportIds = shadowLoadReports.map((report) => report.reportId);
        if (reportIds.length > 0) {
            const reports = await fetchReports(email, reportIds);
            setCachedReports(reports);
            setDefaultReport(reports.find(report => report.isDefault) ?? null);
        } else {
            setCachedReports([]);
            setDefaultReport(null);
        }
      } catch (error) {
        alert(`Error loading reports: ${error}`);
      }
    };

    if (email) {
      loadInitialReports();
    }
  }, [email]);

  const sortedReports = useMemo(() => {
    logger.log("Sorting cachedReports...");

    return [...cachedReports].sort((a, b) => {
        if (a.isDefault === b.isDefault) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
    });
  }, [cachedReports, defaultReport]);

  if (!email) {
    return <>{children}</>; // email无效时不抛错，直接不fetch
  }

  return (
    <ReportContext.Provider value={{sortedReports, defaultReport, createReport, changeDefaultReport, deleteReport}}>
      {children}
    </ReportContext.Provider>
  );
};
